import { Popup, Typography } from '@tunasong/ui-lib'
import { formatNumber } from '@tunasong/models'
import { useDragMove } from '@tunasong/ui-lib'
import cn from 'classnames'
import { useCallback, useState } from 'react'
import type { FC, HTMLAttributes, KeyboardEvent, Ref } from 'react'
import { gainAriaValueText, gainToDB, gainToPosition, positionToGain } from '../../lib/fader-taper.js'
import styles from './channel-volume.module.css'

// Interface is defined at the top of the file

const VolumeContainer = ({ ref, ...props }: HTMLAttributes<HTMLDivElement> & { ref?: Ref<HTMLDivElement> }) => (
  <div ref={ref} {...props} className={cn(styles.container, props.className)} style={props.style} />
)

/** The drag listens on this element: without its ref the bar cannot be dragged (GH #2897) */
const VolumeBar = ({ ref, ...props }: HTMLAttributes<HTMLDivElement> & { ref?: Ref<HTMLDivElement> }) => (
  <div ref={ref} {...props} className={cn(styles.bar, props.className)} style={props.style} />
)

interface VolumeLevelProps extends HTMLAttributes<HTMLDivElement> {
  /** The fader's position in [0, 1], not its gain */
  volumeLevel: number
}

const VolumeLevel = ({ volumeLevel, ...props }: VolumeLevelProps) => (
  <div
    {...props}
    className={cn(styles.level, props.className)}
    style={{ width: `${Math.min(volumeLevel * 100, 100)}%`, ...props.style }}
  />
)

/** The gain applied, as before; silence reads -∞dB */
const formatDB = (dB: number) => (Number.isFinite(dB) ? `${formatNumber(dB, { digits: 1 })}dB` : '-∞dB')

export interface ChannelVolumeProps {
  className?: string
  volume: number
  onChangeVolume(volume: number): void
}

export const ChannelVolume: FC<ChannelVolumeProps> = props => {
  const { className, volume, onChangeVolume } = props

  const [ref, setRef] = useState<HTMLDivElement | null>(null)

  /** Dragging the volume bar? */

  const handleVolume = useCallback(
    (ev: MouseEvent) => {
      if (!ref) {
        return
      }
      ev.preventDefault()
      const box = ref.getBoundingClientRect()
      const percentage = Math.max(Math.min(((ev.clientX - box.left) / box.width) * 100, 100), 0)
      /** The bar is the fader's position; the volume is gain, through the dB taper (GH #2897) */
      onChangeVolume(positionToGain(percentage / 100))
    },
    [onChangeVolume, ref]
  )
  const [showDB, setShowDB] = useState(false)
  const handleStart = useCallback(
    (ev: MouseEvent) => {
      handleVolume(ev)
      setShowDB(true)
    },
    [handleVolume]
  )

  const handleStop = useCallback(
    (ev: MouseEvent) => {
      handleVolume(ev)
      setShowDB(false)
    },
    [handleVolume]
  )

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)

  /** Keyboard-operable too: 1% of travel a step (0.4 dB; 1.2 dB below 25%), Home and End to the ends */
  const position = gainToPosition(volume)
  const keySteps: Record<string, (p: number) => number> = {
    ArrowLeft: p => p - 0.01,
    ArrowDown: p => p - 0.01,
    ArrowRight: p => p + 0.01,
    ArrowUp: p => p + 0.01,
    Home: () => 0,
    End: () => 1,
  }
  const handleKeyDown = (ev: KeyboardEvent<HTMLDivElement>) => {
    const step = keySteps[ev.key]
    if (!step) {
      return
    }
    ev.preventDefault()
    /** Rounded to the 1% grid, so repeated steps don't drift */
    onChangeVolume(positionToGain(Math.round(step(position) * 100) / 100))
  }

  useDragMove({
    el: ref,
    onMove: handleVolume,
    onStart: handleStart,
    onStop: handleStop,
  })

  /** The gain applied */
  const db = formatDB(gainToDB(volume))

  return (
    <VolumeContainer className={className} title={db} ref={setAnchorEl}>
      <VolumeBar
        ref={setRef}
        role="slider"
        tabIndex={0}
        aria-label="Volume"
        aria-valuemin={0}
        aria-valuemax={1}
        aria-valuenow={position}
        aria-valuetext={gainAriaValueText(volume)}
        onKeyDown={handleKeyDown}
      >
        <VolumeLevel volumeLevel={position} />
      </VolumeBar>
      <Popup open={showDB} anchorEl={anchorEl} placement="top">
        <Typography variant="caption">{db}</Typography>
      </Popup>
    </VolumeContainer>
  )
}

export default ChannelVolume
